import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Search, Image as ImageIcon, TriangleAlert, Trash2, CheckCircle2, Wand2 } from 'lucide-react';
import Button from './ui/Button';
import FichaDialog from './ui/FichaDialog';
import { searchMedia, uploadMedia, fetchMediaArchivoDataUrl, reportarFalloSubida } from '../utils/productApi';
import {
  normalizeFilename, generateAltImagen, validarImagen, procesarImagenParaSubir,
} from '../utils/productImages';
import { encuadreRatio, colorDeBordes } from '../utils/imagenAjuste45';
import './ProductImageSelector.css';

// P2b tanda 3 — selector de imagen con dos vías (subir / mediateca), en un diálogo.
// Tanda 3c (ajuste 7): la subida acepta VARIAS imágenes a la vez. onSelect recibe
// SIEMPRE un array de { id, src, alt } (una o varias); el diálogo se cierra desde
// acá (con onClose) tras entregar, para poder quedarse abierto si alguna falla.
// P3-1 (D1): lo que se sube desde el DISPOSITIVO se convierte UNA vez a JPG 0.85
// (procesarImagenParaSubir); la mediateca no se toca. Un archivo que el navegador
// NO decodifica (HEIC de celular) entra a la lista como INCOMPATIBLE con su
// aviso — nunca falla mudo — y no se sube. Tanda 3e (ajuste 8): una foto que no
// es 4:5 se puede RECORTAR (marco móvil) o RELLENAR con margen del color del
// fondo, con vista previa, antes de la conversión y la compresión.

function baseSlug(nombre, variante) {
  return normalizeFilename(nombre, variante).replace(/\.(jpg|jpeg|webp)$/i, '');
}

function cargarImagen(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Detecta el color del fondo en los bordes (canvas chico); si el navegador no
// da canvas, blanco — el selector de color deja corregirlo igual.
async function detectarColorDeFondo(dataUrl) {
  try {
    const img = await cargarImagen(dataUrl);
    const S = 64;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    const ctx = c.getContext && c.getContext('2d');
    if (!ctx) return '#ffffff';
    ctx.drawImage(img, 0, 0, S, S);
    const d = ctx.getImageData(0, 0, S, S);
    return colorDeBordes(d.data, S, S);
  } catch {
    return '#ffffff';
  }
}

// P3-1 (punto 4) — EDITOR DE ENCUADRE unificado: acercar/alejar y mover la foto
// dentro de un lienzo 4:5 fijo (zoom<1 agrega margen del color detectado;
// zoom>1 recorta). Se ofrece SIEMPRE — también cuando la foto ya es 4:5: el
// caso medido en la Fase A era una foto 4:5 con el producto chico sobre fondo
// blanco, sin ninguna forma de acercarlo. La vista previa es un CANVAS real:
// el marco absoluto por porcentajes + box-shadow de 400px de la 3e se corría y
// dejaba bandas sin oscurecer (el defecto visual que Miguel vio en producción).
// P3-12: la proporción por defecto del encuadre (4:5) — la prenda, y el fallback si no llega prop.
const PROP_FOTO_DEFAULT = { ratioW: 4, ratioH: 5, maxW: 1600, maxH: 2000, label: '4:5' };

function AjustadorEncuadre({ item, onCambiar, onCancelar, onAplicar, prop = PROP_FOTO_DEFAULT }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const arrastre = useRef(null); // { x0, y0, cx0, cy0 } al empezar a arrastrar
  const ed = item.editor; // { zoom, cx, cy, color }
  const PREVIEW_W = 240;

  useEffect(() => {
    let vivo = true;
    const dibujar = (img) => {
      const c = canvasRef.current;
      if (!c || !vivo) return;
      const e = encuadreRatio(img.naturalWidth || 1, img.naturalHeight || 1, { ...ed, ratioW: prop.ratioW, ratioH: prop.ratioH, maxW: prop.maxW, maxH: prop.maxH });
      c.width = PREVIEW_W; c.height = (PREVIEW_W / prop.ratioW) * prop.ratioH;
      const ctx = c.getContext && c.getContext('2d');
      if (!ctx) return; // sin canvas no hay preview; Aplicar informará el motivo
      const esc = PREVIEW_W / e.canvasW;
      ctx.fillStyle = ed.color || '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, e.dx * esc, e.dy * esc, e.dw * esc, e.dh * esc);
    };
    if (imgRef.current) dibujar(imgRef.current);
    else cargarImagen(item.dataUrl).then((img) => { imgRef.current = img; dibujar(img); }).catch(() => {});
    return () => { vivo = false; };
  }, [ed, item.dataUrl, prop]);

  const alMover = (e) => {
    const a = arrastre.current;
    const r = canvasRef.current?.getBoundingClientRect();
    if (!a || !r || !r.width) return;
    const nx = a.cx0 + (e.clientX - a.x0) / r.width;
    const ny = a.cy0 + (e.clientY - a.y0) / r.height;
    onCambiar({ cx: Math.min(1, Math.max(0, nx)), cy: Math.min(1, Math.max(0, ny)) });
  };

  return (
    <div className="pis-ajuste">
      <p className="pis-ajuste-h">
        Acerca, aleja y mueve el producto; lo que ves en el marco blanco {prop.label} es lo que queda.
      </p>
      <canvas
        ref={canvasRef} className="pis-encuadre-lienzo" aria-label={`Vista previa del encuadre ${prop.label}`}
        onPointerDown={(e) => { arrastre.current = { x0: e.clientX, y0: e.clientY, cx0: ed.cx, cy0: ed.cy }; e.currentTarget.setPointerCapture?.(e.pointerId); }}
        onPointerMove={(e) => { if (arrastre.current) alMover(e); }}
        onPointerUp={() => { arrastre.current = null; }}
      />
      <label className="pis-label pis-ajuste-ctrl">Acercar / alejar
        <input type="range" min="25" max="300" value={Math.round((ed.zoom || 1) * 100)}
          onChange={(e) => onCambiar({ zoom: Number(e.target.value) / 100 })} />
      </label>
      <label className="pis-label pis-ajuste-ctrl">Color del fondo
        <input type="color" value={ed.color} onChange={(e) => onCambiar({ color: e.target.value })} />
      </label>
      <div className="pis-ajuste-actions">
        <Button size="sm" onClick={onCancelar}>Cancelar</Button>
        <Button size="sm" variant="primary" onClick={onAplicar}>Aplicar encuadre {prop.label}</Button>
      </div>
    </div>
  );
}

// P3-1 (punto 5): `fotosDelProducto` — las fotos que el producto YA tiene
// (principal + galería + asignadas a variaciones), para que una variación se
// asocie POR ID sin volver a subir nada. Cuando llega, es la PRIMERA pestaña.
// `contexto` = { productoId, variacionId } para el registro de fallos (punto 6).
export default function ProductImageSelector({ open, onClose, onSelect, nombre = '', variante = '', fotosDelProducto = null, contexto = null, proporcion = PROP_FOTO_DEFAULT }) {
  const hayFotosProducto = Array.isArray(fotosDelProducto) && fotosDelProducto.length > 0;
  const [modo, setModo] = useState('subir'); // 'producto' | 'subir' | 'mediateca'
  const fileRef = useRef(null);

  // Subir (ajuste 7): lista de imágenes elegidas, cada una con su nombre/alt.
  // { key, dataUrl, mime, dims, filename, alt, status:'pendiente'|'subiendo'|'ok'|'error', error }
  const [items, setItems] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);

  // Mediateca
  const [q, setQ] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setModo(hayFotosProducto ? 'producto' : 'subir'); setItems([]); setError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buscar = useCallback(async (pg = 1) => {
    setCargando(true);
    try {
      const r = await searchMedia({ search: q, page: pg });
      setMediaItems(r.items || []); setTotalPages(r.totalPages || 1); setPage(pg);
    } catch (e) { setError(e.message); }
    setCargando(false);
  }, [q]);

  useEffect(() => { if (open && modo === 'mediateca' && mediaItems.length === 0) buscar(1); }, [open, modo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agrega los archivos elegidos a la lista (cada uno con su miniatura, nombre y alt).
  // P3-1 (bug C, medido): si el navegador NO decodifica el archivo (HEIC de
  // celular en Chrome/Edge), img.onload no dispara NUNCA y el ítem no aparecía
  // — falla muda. Ahora onerror lo agrega como INCOMPATIBLE, con el motivo.
  const agregarArchivos = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError('');
    const base = baseSlug(nombre, variante);
    files.forEach((file) => {
      const agregarIncompatible = () => {
        seq.current += 1;
        setItems((prev) => [...prev, {
          key: `f${seq.current}`,
          dataUrl: null, mime: file.type || '', dims: null,
          filename: file.name || 'archivo',
          alt: '', status: 'incompatible',
          error: 'Formato no compatible. Exporta la foto como JPG desde tu celular.',
          original: null, editor: null,
        }]);
        // Punto 6: el fallo de CONVERSIÓN también queda registrado.
        reportarFalloSubida({
          paso: 'conversion', archivo: file.name || 'archivo', mime: file.type || '',
          tamano: file.size || null, motivo: 'El navegador no decodificó el archivo (formato no compatible)',
          productoId: contexto?.productoId ?? null, variacionId: contexto?.variacionId ?? null,
        });
      };
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target.result;
        const img = new Image();
        img.onload = () => {
          setItems((prev) => {
            const n = prev.length + 1;                       // sufijo -1, -2, …
            seq.current += 1;
            return [...prev, {
              key: `f${seq.current}`,
              dataUrl: url, mime: file.type || 'image/jpeg',
              dims: { width: img.naturalWidth, height: img.naturalHeight },
              // P3-1 (D1): la subida convierte a JPG → el nombre lo dice.
              filename: `${base}-${n}.jpg`,
              alt: generateAltImagen(nombre, variante),
              status: 'pendiente', error: '',
              original: null,      // 3e (ajuste 8): para "Volver a la original"
              editor: null,        // 3e (ajuste 8): editor de proporción abierto
            }];
          });
        };
        img.onerror = agregarIncompatible;
        img.src = url;
      };
      reader.onerror = agregarIncompatible;
      reader.readAsDataURL(file);
    });
  };

  const setItem = (key, patch) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const quitarItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  // ── Encuadre 4:5 (P3-1) — por foto, con vista previa en canvas ─────────────
  const abrirEditor = async (key) => {
    const it = items.find((x) => x.key === key);
    if (!it) return;
    const color = await detectarColorDeFondo(it.dataUrl);
    setItem(key, { editor: { zoom: 1, cx: 0.5, cy: 0.5, color } });
  };
  const volverOriginal = (key) => setItems((prev) => prev.map((it) => (
    it.key === key && it.original
      ? { ...it, dataUrl: it.original.dataUrl, dims: it.original.dims, original: null, editor: null }
      : it
  )));
  const aplicarEditor = async (key) => {
    const it = items.find((x) => x.key === key);
    if (!it || !it.editor) return;
    try {
      const img = await cargarImagen(it.dataUrl);
      const e = encuadreRatio(it.dims.width, it.dims.height, { ...it.editor, ratioW: proporcion.ratioW, ratioH: proporcion.ratioH, maxW: proporcion.maxW, maxH: proporcion.maxH });
      const c = document.createElement('canvas');
      c.width = e.canvasW; c.height = e.canvasH;
      const ctx = c.getContext && c.getContext('2d');
      if (!ctx) throw new Error('el navegador no dio canvas');
      ctx.fillStyle = it.editor.color || '#ffffff';
      ctx.fillRect(0, 0, e.canvasW, e.canvasH);
      ctx.drawImage(img, e.dx, e.dy, e.dw, e.dh);
      // El encuadre se aplica ANTES de la conversión y la compresión: acá
      // queda PNG sin pérdida; el JPG y el peso llegan al subir.
      setItem(key, {
        original: it.original || { dataUrl: it.dataUrl, dims: it.dims },
        dataUrl: c.toDataURL('image/png'),
        dims: { width: e.canvasW, height: e.canvasH },
        editor: null,
      });
    } catch (err) {
      // P3-1: el motivo SIEMPRE visible — «no muestra nada y sale error» sin
      // decir cuál era exactamente la queja de producción.
      setItem(key, { editor: null });
      setError(`No se pudo aplicar el encuadre: ${err?.message || 'error del navegador'}.`);
    }
  };

  // P3-1 (punto 4): encuadrar una foto de la MEDIATECA — el archivo se trae por
  // el BACKEND (/api/catalog/media/:id/archivo): la URL pública de WordPress es
  // cross-origin y mancharía el canvas (SecurityError al exportar). La foto
  // entra a la pestaña Subir como pendiente; al subirla queda como archivo
  // NUEVO en la mediateca (la original no se toca).
  const ajustarDesdeMediateca = async (m) => {
    setError('');
    try {
      const dataUrl = await fetchMediaArchivoDataUrl(m.id);
      const img = await cargarImagen(dataUrl);
      seq.current += 1;
      setItems((prev) => [...prev, {
        key: `f${seq.current}`,
        dataUrl, mime: 'image/jpeg',
        dims: { width: img.naturalWidth, height: img.naturalHeight },
        filename: `${baseSlug(nombre, variante)}-${items.length + 1}.jpg`,
        alt: m.alt || generateAltImagen(nombre, variante),
        status: 'pendiente', error: '', original: null, editor: null,
      }]);
      setModo('subir');
    } catch (e) {
      setError(`No se pudo preparar la imagen de la mediateca: ${e.message}`);
    }
  };

  // Sube todas las pendientes/erradas. Si una falla, se marca y las demás siguen.
  const subirTodo = async () => {
    const aSubir = items.filter((it) => it.status === 'pendiente' || it.status === 'error');
    if (!aSubir.length) return;
    setSubiendo(true); setError('');
    const entregadas = [];
    for (const it of aSubir) {
      setItem(it.key, { status: 'subiendo', error: '' });
      try {
        // P3-1 (D1): UNA conversión a JPG 0.85, techo 1600×2000 y ≤400 KB
        // (después del ajuste de proporción, si lo hubo). La mediateca no pasa
        // por acá. El reintento WebP→JPG de la 3f se retiró con el WebP.
        const finalUrl = await procesarImagenParaSubir(it.dataUrl, { maxW: proporcion.maxW, maxH: proporcion.maxH });
        const b64 = finalUrl.split(',')[1];
        const finalMime = finalUrl.substring(5, finalUrl.indexOf(';')) || it.mime;
        const saved = await uploadMedia({ imageBase64: b64, imageMime: finalMime, filename: it.filename, altText: it.alt });
        setItem(it.key, { status: 'ok' });
        // P3-9b (remate): recién subida por Netto (ya pasó por la tubería JPG) →
        // «Optimizada» en vivo, sin esperar a recargar /full. El backend la registra
        // igual (origen 'subida'). La selección de mediateca NO pasa por acá.
        entregadas.push({ id: saved.id, src: saved.src, alt: saved.alt, optimizada: true });
      } catch (e) {
        setItem(it.key, { status: 'error', error: e.message });
        // Punto 6 (D6): cada fallo queda en subidas_imagenes con su motivo real.
        reportarFalloSubida({
          paso: 'mediateca', archivo: it.filename, mime: it.mime,
          tamano: it.dataUrl ? Math.round((it.dataUrl.length - it.dataUrl.indexOf(',') - 1) * 3 / 4) : null,
          http: e.http ?? null, codigo: e.code ?? null, motivo: e.message,
          productoId: contexto?.productoId ?? null, variacionId: contexto?.variacionId ?? null,
        });
      }
    }
    setSubiendo(false);
    if (entregadas.length) onSelect(entregadas);           // agrega las que sí subieron
    // Si todas subieron, cierra; si alguna falló, deja el diálogo abierto con el
    // detalle de cuál (las ok ya se quitan de la lista para no re-subirlas).
    setItems((prev) => prev.filter((it) => it.status !== 'ok'));
    setTimeout(() => {
      setItems((cur) => { if (cur.length === 0) onClose(); return cur; });
    }, 0);
  };

  if (!open) return null;
  const hayNombre = String(nombre || '').trim();
  const pendientes = items.filter((it) => it.status === 'pendiente' || it.status === 'error').length;

  return (
    // Tanda 7c (punto 1): sobre el diálogo base de la ficha. El esqueleto que
    // FichaDialog trae (88vh, cuerpo con scroll interno) ES el de este diálogo,
    // que fue el aprobado; las pestañas van como subheader (fila fija).
    <FichaDialog
      titulo="Imagen del producto"
      ariaLabel="Seleccionar imagen"
      onClose={onClose}
      className="pis"
      subheader={(
        <>
          {/* El input de archivo vive FUERA de las pestañas (siempre montado con el
              diálogo abierto). Ajuste 7: acepta varias imágenes (multiple). */}
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={(e) => { agregarArchivos(e.target.files); e.target.value = ''; }} />
          <div className="pis-tabs" role="tablist">
          {hayFotosProducto && (
            <button role="tab" aria-selected={modo === 'producto'} className={`pis-tab${modo === 'producto' ? ' is-active' : ''}`} onClick={() => setModo('producto')}>
              <ImageIcon size={15} /> Fotos de este producto
            </button>
          )}
          <button
            role="tab" aria-selected={modo === 'subir'} className={`pis-tab${modo === 'subir' ? ' is-active' : ''}`}
            onClick={() => {
              setModo('subir');
              if (items.length === 0 && hayNombre) fileRef.current?.click();
            }}
          ><Upload size={15} /> Subir archivo</button>
          <button role="tab" aria-selected={modo === 'mediateca'} className={`pis-tab${modo === 'mediateca' ? ' is-active' : ''}`} onClick={() => setModo('mediateca')}><ImageIcon size={15} /> Mediateca</button>
          </div>

          {error && <div className="pis-error">{error}</div>}
        </>
      )}
    >
      {modo === 'producto' && hayFotosProducto && (
        <>
          <p className="pis-rename-note">
            Elige una foto que el producto ya tiene: se asocia tal cual, sin volver a subirla.
          </p>
          <div className="pis-grid">
            {fotosDelProducto.map((m) => (
              <button key={m.id} className="pis-cell" title={m.etiqueta || m.alt || ''}
                onClick={() => { onSelect([{ id: m.id, src: m.src, alt: m.alt || '' }]); onClose(); }}>
                <img src={m.src} alt={m.alt || m.etiqueta || 'Foto del producto'} loading="lazy" />
                {m.etiqueta && <span className="pis-cell-tag">{m.etiqueta}</span>}
              </button>
            ))}
          </div>
        </>
      )}
      {modo === 'subir' && (
        <>
            {!hayNombre ? (
              <div className="pis-need-name" role="alert">
                <TriangleAlert size={20} aria-hidden="true" />
                <div>
                  <strong>Escribe primero el nombre del producto.</strong>
                  <p>
                    La foto se guarda en WordPress con un nombre de archivo generado a partir
                    del nombre del producto (por ejemplo <code>marca-nombre-del-producto.jpg</code>),
                    aunque tu archivo se llame distinto. El texto alternativo también se genera de él.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Ajuste 8: por qué se renombra el archivo. P3-1: JPG directo. */}
                <p className="pis-rename-note">
                  Google lee el nombre del archivo: <code>IMG_4821.jpg</code> no dice nada,
                  {' '}<code>marca-nombre-del-producto.jpg</code> sí. Por eso cada foto se sube con un
                  nombre generado del producto (lo puedes editar) y comprimida para que cargue rápido.
                </p>
                {items.length === 0 ? (
                  <button className="pis-drop" onClick={() => fileRef.current?.click()}>
                    <Upload size={28} /><span>Elige una o varias fotos del dispositivo</span>
                    <small>En el celular puedes tomar la foto o elegirla de la galería. Puedes seleccionar varias a la vez.</small>
                  </button>
                ) : (
                  <>
                    <ul className="pis-list">
                      {items.map((it) => {
                        // P3-1: un archivo que el navegador no decodificó (HEIC)
                        // se LISTA con su aviso y el botón de quitar — sin
                        // miniatura ni campos, y nunca se sube.
                        if (it.status === 'incompatible') {
                          return (
                            <li key={it.key} className="pis-lrow pis-lrow--error">
                              <div className="pis-lthumb" aria-hidden="true" />
                              <div className="pis-lfields">
                                <p className="pis-error-row">«{it.filename}»: {it.error}</p>
                              </div>
                              <button type="button" className="pis-lremove" aria-label={`Quitar ${it.filename}`} onClick={() => quitarItem(it.key)}>
                                <Trash2 size={16} />
                              </button>
                            </li>
                          );
                        }
                        const val = it.dims ? validarImagen(it.dims.width, it.dims.height) : null;
                        return (
                          <li key={it.key} className={`pis-lrow pis-lrow--${it.status}`}>
                            <img className="pis-lthumb" src={it.dataUrl} alt="Vista previa" />
                            <div className="pis-lfields">
                              {it.dims && <p className="pis-dims">{it.dims.width} × {it.dims.height} px</p>}
                              {val && !val.ok && <p className="pis-warn">Recomendado corregir: {val.reasons.join('; ')}. Puedes continuar igual.</p>}
                              {/* P3-1 (punto 4): el encuadre se ofrece SIEMPRE —
                                  también sobre una foto ya 4:5 (acercar el
                                  producto era imposible antes). */}
                              {!it.editor && it.status !== 'subiendo' && (
                                <div className="pis-fix">
                                  <Button size="sm" icon={<Wand2 size={14} />} onClick={() => abrirEditor(it.key)}>Encuadrar ({proporcion.label})</Button>
                                  {it.original && <Button size="sm" onClick={() => volverOriginal(it.key)}>Volver a la original</Button>}
                                </div>
                              )}
                              {it.editor && (
                                <AjustadorEncuadre
                                  item={it}
                                  prop={proporcion}
                                  onCambiar={(patch) => setItem(it.key, { editor: { ...it.editor, ...patch } })}
                                  onCancelar={() => setItem(it.key, { editor: null })}
                                  onAplicar={() => aplicarEditor(it.key)}
                                />
                              )}
                              <label className="pis-label">Nombre del archivo</label>
                              <input className="pis-input" value={it.filename} disabled={subiendo}
                                onChange={(e) => setItem(it.key, { filename: e.target.value })} />
                              <label className="pis-label">Texto alternativo</label>
                              <input className="pis-input" value={it.alt} disabled={subiendo}
                                onChange={(e) => setItem(it.key, { alt: e.target.value })} />
                              {it.status === 'error' && (
                                <p className="pis-error-row">
                                  No se pudo subir «{it.filename}»: {it.error}.
                                  {' '}<Button size="sm" onClick={subirTodo} disabled={subiendo}>Reintentar</Button>
                                </p>
                              )}
                              {it.status === 'ok' && <p className="pis-ok-row"><CheckCircle2 size={13} aria-hidden="true" /> Subida.</p>}
                            </div>
                            {!subiendo && (
                              <button type="button" className="pis-lremove" aria-label={`Quitar ${it.filename}`} onClick={() => quitarItem(it.key)}>
                                <Trash2 size={16} />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <div className="pis-actions">
                      <Button onClick={() => fileRef.current?.click()} disabled={subiendo}>Agregar más</Button>
                      <Button variant="primary" loading={subiendo} disabled={pendientes === 0}
                        onClick={subirTodo}>
                        Subir y usar {pendientes} {pendientes === 1 ? 'imagen' : 'imágenes'}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
        </>
      )}
      {modo === 'mediateca' && (
        <>
            <div className="pis-search">
              <input className="pis-input" placeholder="Buscar por nombre…" value={q}
                onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') buscar(1); }} />
              <Button icon={<Search size={15} />} onClick={() => buscar(1)} loading={cargando}>Buscar</Button>
            </div>
            <div className="pis-grid">
              {mediaItems.map((m) => (
                <div key={m.id} className="pis-cellwrap">
                  <button className="pis-cell" title={m.title || m.alt} onClick={() => { onSelect([{ id: m.id, src: m.full || m.src, alt: m.alt }]); onClose(); }}>
                    <img src={m.src} alt={m.alt} loading="lazy" />
                  </button>
                  {/* P3-1 (punto 4): encuadrar también las de la mediateca. */}
                  <button type="button" className="pis-cell-ajustar" disabled={!hayNombre}
                    title={hayNombre ? 'Trae la foto para acercar/alejar y subirla encuadrada' : 'Escribe primero el nombre del producto'}
                    onClick={() => ajustarDesdeMediateca(m)}>
                    <Wand2 size={12} aria-hidden="true" /> Encuadrar antes de usar
                  </button>
                </div>
              ))}
              {!cargando && mediaItems.length === 0 && <p className="pis-muted">Sin resultados.</p>}
            </div>
            {totalPages > 1 && (
              <div className="pis-pager">
                <Button size="sm" disabled={page <= 1} onClick={() => buscar(page - 1)}>Anterior</Button>
                <span>{page} / {totalPages}</span>
                <Button size="sm" disabled={page >= totalPages} onClick={() => buscar(page + 1)}>Siguiente</Button>
              </div>
            )}
        </>
      )}
    </FichaDialog>
  );
}
